import clsx from 'clsx';
import set from 'lodash/set';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { getFQDNFromString } from '../../../../../../_metronic/helpers/AssetHelpers';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import {
  normalizeVpn,
  SiService,
  siteIntelligenceServiceLabel,
  Vpn,
  vpnLabel,
} from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import UrlWithMetadataCard, { UrlWithMetadataFieldType } from '../card/UrlWithMetadataCard';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { AddInOutboundLinksModal } from '../modal/AddInOutboundLinksModal';
import { HistoryModal } from '../modal/HistoryModal';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import { getFieldUpdate, SiRequestUser } from '../utils';

interface Props {
  data: any;
  isSiAiNewData: boolean;
  reviewer?: SiRequestUser[];
  status: string;
  refetch: () => void;
  isEditable?: boolean;
  isSaving?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSave?: UsePageDataManagerResult['saveService'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
}

type LinkedSite = { index?: number; _id?: string; url: string };
type UrlObj = {
  title: string;
  description: string;
  linkedSites: LinkedSite[];
};
type UrlMap = Map<string, UrlObj>;
type GroupedDetails = {
  preview: UrlMap; // one link from each of the first 5 FQDNs
  others: UrlMap; // all other links
};

export default function InboundFlowDetail({
  data,
  isSiAiNewData,
  reviewer,
  status,
  refetch,
  isEditable,
  isSaving,
  onUpdate,
  onSave,
  onReset,
  checkDirty,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_INBOUND_FLOW_DETAIL.${id}` }, values);
  const { setToast } = useToast();
  const [showOtherLinks, setShowOtherLinks] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const maxPreviewLinksPerVpn = 5;
  const emDash = '—';
  const inputFieldClassName = 'form-control form-control-sm fw-normal text-dark';

  const isDirty = checkDirty && checkDirty(SiService.INBOUND_FLOW_SERVICE);
  const inboundFqdnFieldUpdates = getFieldUpdate('result.fqdn_group.fqdn', data?.field_updates);
  const inboundFqdnCountFieldUpdates = getFieldUpdate('result.fqdn_group.count', data?.field_updates);
  const totalFoundLinksFieldUpdates = getFieldUpdate('result.total_found_links', data?.field_updates);
  const checkedLinksFieldUpdates = getFieldUpdate('result.checked_links', data?.field_updates);
  const clickableLinksFieldUpdates = getFieldUpdate('result.clickable_links', data?.field_updates);
  const hasHistory = [
    inboundFqdnFieldUpdates,
    inboundFqdnCountFieldUpdates,
    totalFoundLinksFieldUpdates,
    checkedLinksFieldUpdates,
    clickableLinksFieldUpdates,
  ].some((fieldUpdates) => fieldUpdates.length > 0);
  const getReviewerEmail = (userId?: string) =>
    reviewer?.find((reviewerData) => reviewerData.user_id === userId)?.email ?? userId;
  const fqdnGroupReviewerNames = Array.from(
    new Set(
      [...inboundFqdnFieldUpdates, ...inboundFqdnCountFieldUpdates]
        .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
        .filter(Boolean)
    )
  );
  const totalFoundLinksReviewerNames = Array.from(
    new Set(
      totalFoundLinksFieldUpdates
        .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
        .filter(Boolean)
    )
  );
  const checkedLinksReviewerNames = Array.from(
    new Set(
      [...checkedLinksFieldUpdates, ...clickableLinksFieldUpdates]
        .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
        .filter(Boolean)
    )
  );

  const groupedDetails = useMemo(() => {
    const groupedData: GroupedDetails = { preview: new Map(), others: new Map() };
    const previewFqdnSet = new Set<string>();

    if (!data?.detail?.length) return groupedData;

    data.detail.forEach((item, idx) => {
      const fqdn = getFQDNFromString(item?.found_link);
      const isLinkInPreview = groupedData.preview.has(item?.found_link);
      const isLinkInOthers = groupedData.others.has(item?.found_link);
      const linkedSiteObj: UrlObj['linkedSites'][number] = {
        index: idx,
        _id: item?._id,
        url: item?.source_link,
      };

      if (isLinkInPreview || isLinkInOthers) {
        const existingUrlObj = groupedData[isLinkInPreview ? 'preview' : 'others'].get(
          item?.found_link
        );
        if (!existingUrlObj.linkedSites.some((site) => site.url === item?.source_link)) {
          existingUrlObj.linkedSites.push(linkedSiteObj);
        }
      } else {
        const isPreviewMaxed = groupedData.preview.size >= maxPreviewLinksPerVpn;
        const isFqdnInPreview = !isEditable && previewFqdnSet.has(fqdn);
        const urlObject: UrlObj = {
          title: item?.title,
          description: item?.description,
          linkedSites: [linkedSiteObj],
        };
        if (isPreviewMaxed || isFqdnInPreview) {
          groupedData.others.set(item?.found_link, urlObject);
        } else {
          groupedData.preview.set(item?.found_link, urlObject);
          previewFqdnSet.add(fqdn);
        }
      }
    });
    return groupedData;
  }, [data]);

  const handleOpenAddModal = () => {
    if (isDirty) {
      alert('You have unsaved changes. Please save before proceeding.');
    } else {
      setShowAddModal(true);
    }
  };

  const handleSave = async () => {
    await onSave(SiService.INBOUND_FLOW_SERVICE);
    setIsEditing(false);

    // Scroll to top of card
    const element = document.getElementById('inbound-content');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleReset = () => {
    onReset(SiService.INBOUND_FLOW_SERVICE);
  };

  const handleUpdateLinkData = (
    link: string,
    linkData: UrlObj,
    field: UrlWithMetadataFieldType,
    newValue: string | string[]
  ) => {
    if (['title', 'description'].includes(field)) {
      onUpdate(SiService.INBOUND_FLOW_SERVICE, (currentDraft) => {
        linkData.linkedSites.forEach((site) => {
          set(currentDraft, `detail[${site.index}].${field}`, newValue || null);
        });
      });
    } else if (field === 'url' && typeof newValue === 'string') {
      onUpdate(SiService.INBOUND_FLOW_SERVICE, (currentDraft) => {
        linkData.linkedSites.forEach((site) => {
          set(currentDraft, `detail[${site.index}].found_link`, newValue || null);
        });

        // Update FQDN
        const oldFqdn = getFQDNFromString(link);
        const newFqdn = getFQDNFromString(newValue);
        if (oldFqdn !== newFqdn) {
          let removeOldFqdn = true;
          for (const item of currentDraft.detail) {
            const fqdn = getFQDNFromString(item?.found_link);
            if (fqdn === oldFqdn) {
              removeOldFqdn = false;
              break;
            }
          }

          const fqdnSet = new Set<string>([
            ...(currentDraft?.result?.fqdn_group?.fqdn ?? []),
            newFqdn,
          ]);
          if (removeOldFqdn) {
            fqdnSet.delete(oldFqdn);
          }
          set(currentDraft, 'result.fqdn_group', {
            count: fqdnSet.size,
            fqdn: Array.from(fqdnSet),
          });
        }
      });
    } else if (field === 'linkedSites' && Array.isArray(newValue)) {
      onUpdate(SiService.INBOUND_FLOW_SERVICE, (currentDraft) => {
        const newValueCountMap = new Map<string, number>();
        newValue.forEach((url) => {
          const count = (newValueCountMap.get(url) || 0) + 1;
          newValueCountMap.set(url, count);
        });

        const linkSitesCountMap = new Map<string, number>();
        const missingLinkedSites: LinkedSite[] = [];
        linkData.linkedSites.forEach((site) => {
          const count = (linkSitesCountMap.get(site.url) || 0) + 1;
          linkSitesCountMap.set(site.url, count);
          if (count > (newValueCountMap.get(site.url) ?? 0)) {
            missingLinkedSites.push(site);
          }
        });

        const newLinkedUrlCountMap = new Map<string, number>();
        for (const url of newValue) {
          let isNew = false;
          if (!linkData.linkedSites.some((site) => site.url === url)) {
            isNew = true;
          } else if (
            newValueCountMap.get(url) - linkSitesCountMap.get(url) >
            (newLinkedUrlCountMap.get(url) ?? 0)
          ) {
            isNew = true;
            newLinkedUrlCountMap.set(url, (newLinkedUrlCountMap.get(url) ?? 0) + 1);
          }

          if (!isNew) continue;

          if (missingLinkedSites.length > 0) {
            const missingLinkedSite = missingLinkedSites.shift();
            set(currentDraft, `detail[${missingLinkedSite.index}].source_link`, url || null);
          } else {
            currentDraft.detail.push({
              found_link: link,
              source_link: url,
              title: linkData.title,
              description: linkData.description,
              vpn: Vpn.ASIA,
            });
          }
        }

        if (missingLinkedSites.length > 0) {
          currentDraft.detail = currentDraft.detail.filter(
            (item, idx) =>
              item.found_link !== link ||
              !missingLinkedSites.some((site) => site?.index === idx)
          );
        }
      });
    }
  };

  const handleRemoveLink = (link: string) => {
    onUpdate(SiService.INBOUND_FLOW_SERVICE, (currentDraft) => {
      const newDetail = [];
      const fqdn = getFQDNFromString(link);
      let removeFqdn = true;

      for (const item of currentDraft.detail) {
        if (item.found_link !== link) {
          newDetail.push(item);

          if (removeFqdn && fqdn === getFQDNFromString(item?.found_link)) {
            removeFqdn = false;
          }
        }
      }
      currentDraft.detail = newDetail;

      if (removeFqdn) {
        set(currentDraft, 'result.fqdn_group', {
          count: currentDraft.result.fqdn_group.count - 1,
          fqdn: currentDraft.result.fqdn_group.fqdn.filter((f) => f !== fqdn),
        });
      }
    });
  };

  const dataSection: SummaryRow[] = [
    {
      key: 'fqdnGroup',
      field: t('INBOUND_FQDN', { count: data?.result?.fqdn_group?.count?.toLocaleString() ?? 0 }),
      reviewer:
        (fqdnGroupReviewerNames.length > 0 && isEditable) ? (
          <div className='d-flex align-items-center gap-1 flex-wrap'>
            {fqdnGroupReviewerNames.map((reviewerName) => (
              <Avatar key={reviewerName} name={reviewerName} />
            ))}
          </div>
        ) : undefined,
      render: () => (
        <>
          {data?.result?.fqdn_group?.fqdn?.length > 0 ? (
            <UnlabeledBulletColumns
              data={data.result.fqdn_group.fqdn}
              columnCount={data.result.fqdn_group.fqdn.length > 2 ? 2 : 1}
              shouldBreakText
            />
          ) : (
            emDash
          )}
        </>
      ),
    },
    {
      key: 'totalFoundLinks',
      field: t('FOUND_LINKS'),
      reviewer:
        (totalFoundLinksReviewerNames.length > 0 && isEditable) ? (
          <div className='d-flex align-items-center gap-1 flex-wrap'>
            {totalFoundLinksReviewerNames.map((reviewerName) => (
              <Avatar key={reviewerName} name={reviewerName} />
            ))}
          </div>
        ) : undefined,
      render: () => {
        const value = data?.result?.total_found_links;
        return (
          <>
            {isEditing ? (
              <input
                type='number'
                className={inputFieldClassName}
                value={value ?? ''}
                onChange={(e) =>
                  onUpdate(
                    SiService.INBOUND_FLOW_SERVICE,
                    `result.total_found_links`,
                    +e.target.value
                  )
                }
                min={0}
                step={1}
              />
            ) : (
              value ? t('ABOUT_RESULTS', { count: value.toLocaleString() }) : emDash
            )}
          </>
        );
      },
    },
    ...data?.result?.clickable_links || data?.result?.checked_links ? [{
      key: 'checkedLinks',
      field: t('ACCESSED_SITES'),
      reviewer:
        (checkedLinksReviewerNames.length > 0 && isEditable) ? (
          <div className='d-flex align-items-center gap-1 flex-wrap'>
            {checkedLinksReviewerNames.map((reviewerName) => (
              <Avatar key={reviewerName} name={reviewerName} />
            ))}
          </div>
        ) : undefined,
      render: () => (
        <>
          {data?.result?.clickable_links || data?.result?.checked_links
            ? t('SITES_WITH_CLICKABLE_LINKS', {
              clickable: data?.result?.clickable_links?.toLocaleString() ?? 0,
              checked: data?.result?.checked_links?.toLocaleString() ?? 0,
            })
            : emDash}
        </>
      ),
    }] : [],
  ];

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          isEditable && (
            <>
              {isEditing ? (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    if (isDirty) {
                      handleReset();
                    }
                    setIsEditing(false);
                  }}
                  className={clsx('btn btn-sm btn-secondary')}
                  title='Cancel Edit'
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setIsEditing(true);
                    setShowOtherLinks(true);
                  }}
                  className={clsx('btn btn-sm btn-primary')}
                  title='Edit'
                >
                  Edit
                </button>
              )}
              <div
                className='btn btn-icon btn-sm btn-primary'
                onClick={handleOpenAddModal}
                title={t('ADD_INBOUND_LINKS')}
              >
                <i className='fas fa-plus'></i>
              </div>
            </>
          )
        }
        isEditable={isEditable && isEditing}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={handleSave}
        onReset={handleReset}
      >
        <div className='p-9 vstack gap-9'>
          <SummaryTable
            rows={dataSection}
            data={data}
            refetch={refetch}
            title={<h5 className='fw-bolder m-0'>{t('SUMMARY')}</h5>}
          />

          <div>
            <h5 className='fw-bolder'>
              {isSiAiNewData ? t('LINKS') : t('INSPECTED_CLICKABLE_RESULTS')}
            </h5>

            {groupedDetails.preview.size > 0 ? (
              <div className={clsx('vstack', isEditing ? 'gap-6' : 'gap-4')}>
                {Array.from(groupedDetails.preview, ([link, value], index) => (
                  <div key={`preview-${index}`}>
                    <UrlWithMetadataCard
                      url={link}
                      title={value?.title}
                      description={value?.description}
                      isSiAiNewData={isSiAiNewData}
                      linkedSites={isSiAiNewData ? [] : value?.linkedSites?.map((site) => site.url)}
                      isEditing={isEditing}
                      onUpdate={(field, newValue) => {
                        handleUpdateLinkData(link, value, field, newValue);
                      }}
                      onRemove={() => {
                        handleRemoveLink(link);
                      }}
                    />
                  </div>
                ))}

                {showOtherLinks &&
                  Array.from(groupedDetails.others, ([link, value], index) => (
                    <div key={`others-${index}`}>
                      <UrlWithMetadataCard
                        url={link}
                        title={value?.title}
                        description={value?.description}
                        isSiAiNewData={isSiAiNewData}
                        linkedSites={isSiAiNewData ? [] : value?.linkedSites?.map((site) => site.url)}
                        isEditing={isEditing}
                        onUpdate={(field, newValue) => {
                          handleUpdateLinkData(link, value, field, newValue);
                        }}
                        onRemove={() => {
                          handleRemoveLink(link);
                        }}
                      />
                    </div>
                  ))}
              </div>
            ) : (
              <div>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })}</div>
            )}

            {groupedDetails.others.size > 0 && (
              <div className='mt-4'>
                <button
                  className='btn btn-link btn-color-muted btn-active-color-primary btn-sm p-0'
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setShowOtherLinks((prev) => !prev);
                  }}
                >
                  {showOtherLinks ? (
                    <>
                      {t('SHOW_LESS')} <i className='bi bi-chevron-compact-up fs-3'></i>
                    </>
                  ) : (
                    <>
                      {t('SHOW_ALL_MORE', { remaining: groupedDetails.others.size })}
                      <i className='bi bi-chevron-compact-down fs-3 ms-1' />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      {isEditable && (
        <AddInOutboundLinksModal
          data={data}
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          handleUpdate={onUpdate}
          handleSave={onSave}
        />
      )}

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={{ results: { users: reviewer ?? [] } }}
        service_paths={[
          {
            ref_id: data?._id,
            paths: [
              'result.fqdn_group.fqdn',
              'result.fqdn_group.count',
              'result.total_found_links',
              'result.checked_links',
              'result.clickable_links',
            ],
          },
        ]}
      />
    </>
  );
}
